import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tooltip } from "antd";
import parse from "html-react-parser";
import {
  ArrowLeft,
  Calendar,
  User,
  RotateCw,
  Users,
  Send,
  Search,
  Play,
  Pause,
  XCircle,
  RefreshCw,
  Info,
} from "lucide-react";
import {
  getMailMergeCampaignById,
  startMailMergeCampaign,
  pauseMailMergeCampaign,
  cancelMailMergeCampaign,
  retryFailedMailMergeCampaign,
} from "../services/mailMergeService";
import EmailForm from "../common/EmailForm";
import { formatDateTime } from "../utils";

const MailMergeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCampaign = async () => {
    setLoading(true);
    try {
      const data = await getMailMergeCampaignById(id);
      setCampaign(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCampaign();
    }
  }, [id]);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  const filteredRecipients = useMemo(() => {
    if (!campaign?.recipients) return [];
    if (!searchTerm.trim()) return campaign.recipients;
    const q = searchTerm.toLowerCase();
    return campaign.recipients.filter(
      (r) =>
        r.name?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.status?.toLowerCase().includes(q) ||
        ((r.status?.toLowerCase() === "failed" ||
          r.status?.toLowerCase() === "bounced" ||
          r.status?.toLowerCase() === "limit exceed" ||
          r.status?.toLowerCase() === "limit exceeded" ||
          r.status?.toLowerCase() === "limit_exceeded") &&
          ("limit exceed".includes(q) || "limit exceeded".includes(q))),
    );
  }, [campaign, searchTerm]);

  const totalPages = Math.ceil(filteredRecipients.length / pageSize) || 1;
  const paginatedRecipients = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRecipients.slice(start, start + pageSize);
  }, [filteredRecipients, currentPage]);

  const getInitials = (str) => {
    if (!str) return "L";
    const parts = str.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return str.slice(0, 2).toUpperCase();
  };

  const renderStatusBadge = (status, recipient = {}) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return (
          <span className="tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-md tw-text-[11px] tw-font-semibold tw-bg-[#ECFDF5] tw-text-[#007A55] tw-font-inter">
            Delivered
          </span>
        );
      case "failed":
      case "bounced":
      case "limit exceed":
      case "limit exceeded":
      case "limit_exceeded": {
        const infoMessage =
          recipient?.failureReason ||
          recipient?.errorMessage ||
          recipient?.reason ||
          recipient?.error ||
          recipient?.statusMessage ||
          recipient?.info ||
          recipient?.message ||
          "Message failed to send. Check configuration or try again later.";

        return (
          <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-0.5 tw-rounded-md tw-text-[11px] tw-font-semibold tw-bg-red-50 tw-text-red-700 tw-font-inter">
            <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
            <Tooltip title={infoMessage} placement="top">
              <span className="tw-inline-flex tw-items-center tw-cursor-pointer tw-text-red-500 hover:tw-text-red-700 tw-transition-colors">
                <Info size={12} className="tw-stroke-[2.5]" />
              </span>
            </Tooltip>
          </span>
        );
      }
      default:
        return (
          <span className="tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-md tw-text-[11px] tw-font-semibold tw-bg-slate-100 tw-text-slate-600 tw-font-inter">
            {status || "Sent"}
          </span>
        );
    }
  };

  // Helper to render email body with parsed HTML and highlighted merge tags like {{First Name}}
  const renderFormattedBody = (text) => {
    if (!text) return null;

    let content = text;

    // Unescape doubly-escaped HTML entities if present (e.g. &lt;p&gt; or &lt;div&gt;)
    if (
      /&lt;\s*(div|p|br|span|html|body|table|tr|td|blockquote|a|h[1-6])/i.test(
        content
      )
    ) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = content;
      content = tempDiv.textContent || tempDiv.innerText || content;
    }

    const hasHtmlTags = /<\s*([a-z][a-z0-9]*)\b[^>]*>/i.test(content);
    if (!hasHtmlTags) {
      content = content.replace(/\n/g, "<br/>");
    }

    const options = {
      replace(domNode) {
        if (domNode.type === "text" && /\{\{[^}]+\}\}/.test(domNode.data)) {
          const parts = domNode.data.split(/(\{\{[^}]+\}\})/g);
          return (
            <>
              {parts.map((part, idx) => {
                if (part.startsWith("{{") && part.endsWith("}}")) {
                  return (
                    <span
                      key={idx}
                      className="tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-md tw-bg-[#EEF0FF] tw-text-[#5D44FF] tw-font-semibold tw-text-[11px] tw-mx-0.5 tw-border tw-border-solid tw-border-[#DDE2FF]"
                    >
                      {part}
                    </span>
                  );
                }
                return part;
              })}
            </>
          );
        }
      },
    };

    return (
      <div className="mail-merge-email-content tw-text-xs tw-leading-relaxed tw-text-slate-700 tw-font-inter tw-break-words tw-max-w-full [&_p]:tw-mb-2 last:[&_p]:tw-mb-0 [&_img]:tw-max-w-full [&_img]:tw-h-auto">
        {parse(content, options)}
      </div>
    );
  };

  const deliveryPercentage = campaign?.recipientsCount
    ? Math.round(((campaign?.sentCount || 0) / campaign.recipientsCount) * 100)
    : 0;

  return (
    <div className="p-20 tw-font-inter tw-bg-white tw-min-h-full tw-overflow-y-auto">
      <div className="tw-max-w-[1400px] tw-mx-auto">
        {/* Top Header Section */}
        <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-4 tw-mb-6">
          <div className="tw-flex tw-items-center tw-gap-4">
            <div
              className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-xl tw-p-2.5 tw-bg-slate-100 hover:tw-bg-slate-200/60 tw-text-slate-600 tw-transition-colors tw-flex-shrink-0"
              onClick={() => navigate("/mail-merge")}
              role="button"
              aria-label="Back"
            >
              <ArrowLeft size={18} />
            </div>
            <div>
              <h1 className="tw-text-xl tw-font-bold tw-text-slate-800 tw-font-inter tw-tracking-tight tw-mb-1">
                {campaign?.name || "Mail Merge Campaign"}
              </h1>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-text-xs tw-text-slate-500 tw-font-medium tw-font-inter">
                {(campaign?.sentAt || campaign?.createdAt) && (
                  <span className="tw-flex tw-items-center tw-gap-1.5">
                    <Calendar size={13} className="tw-text-slate-400" />
                    Sent: {formatDateTime(campaign?.sentAt || campaign?.createdAt)}
                  </span>
                )}
                {campaign?.sentBy && (
                  <>
                    {(campaign?.sentAt || campaign?.createdAt) && (
                      <span className="tw-size-1 tw-rounded-full tw-bg-slate-300" />
                    )}
                    <span className="tw-flex tw-items-center tw-gap-1.5">
                      <User size={13} className="tw-text-slate-400" />
                      Sent by: {campaign.sentBy}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            {campaign?.status === "PAUSED" && (
              <button
                type="button"
                onClick={async () => {
                  await startMailMergeCampaign(campaign.id);
                  fetchCampaign();
                }}
                className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-rounded-xl tw-bg-white tw-border tw-border-solid tw-border-slate-200 hover:tw-bg-slate-50 tw-text-slate-700 hover:tw-text-[#12B76A] tw-font-semibold tw-text-xs tw-shadow-sm tw-transition-all tw-cursor-pointer tw-shrink-0 active:tw-scale-95 tw-font-inter"
              >
                <Play size={13} className="tw-stroke-[2.5]" />
                <span>Resume</span>
              </button>
            )}
            {(campaign?.status === "RUNNING" || campaign?.status === "CREATED") && (
              <button
                type="button"
                onClick={async () => {
                  await pauseMailMergeCampaign(campaign.id);
                  fetchCampaign();
                }}
                className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-rounded-xl tw-bg-white tw-border tw-border-solid tw-border-slate-200 hover:tw-bg-slate-50 tw-text-slate-700 hover:tw-text-[#F59E0B] tw-font-semibold tw-text-xs tw-shadow-sm tw-transition-all tw-cursor-pointer tw-shrink-0 active:tw-scale-95 tw-font-inter"
              >
                <Pause size={13} className="tw-stroke-[2.5]" />
                <span>Pause</span>
              </button>
            )}
            {(campaign?.status === "RUNNING" || campaign?.status === "CREATED" || campaign?.status === "PAUSED") && (
              <button
                type="button"
                onClick={async () => {
                  if (window.confirm("Are you sure you want to cancel this campaign?")) {
                    await cancelMailMergeCampaign(campaign.id);
                    fetchCampaign();
                  }
                }}
                className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-rounded-xl tw-bg-white tw-border tw-border-solid tw-border-slate-200 hover:tw-bg-slate-50 tw-text-slate-700 hover:tw-text-red-500 tw-font-semibold tw-text-xs tw-shadow-sm tw-transition-all tw-cursor-pointer tw-shrink-0 active:tw-scale-95 tw-font-inter"
              >
                <XCircle size={13} className="tw-stroke-[2.5]" />
                <span>Cancel</span>
              </button>
            )}
            {(campaign?.status === "COMPLETED" ||
              campaign?.status === "PAUSED" ||
              campaign?.status === "FAILED" ||
              campaign?.status === "LIMIT_EXCEEDED") &&
              (campaign?.failedCount > 0 ||
                campaign?.status === "FAILED" ||
                campaign?.status === "LIMIT_EXCEEDED") && (
              <button
                type="button"
                onClick={async () => {
                  await retryFailedMailMergeCampaign(campaign.id);
                  fetchCampaign();
                }}
                className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-rounded-xl tw-bg-white tw-border tw-border-solid tw-border-slate-200 hover:tw-bg-slate-50 tw-text-slate-700 hover:tw-text-[#5D44FF] tw-font-semibold tw-text-xs tw-shadow-sm tw-transition-all tw-cursor-pointer tw-shrink-0 active:tw-scale-95 tw-font-inter"
              >
                <RefreshCw size={13} className="tw-stroke-[2.5]" />
                <span>Retry Failed</span>
              </button>
            )}

            {/* Reuse Email Button */}
            <EmailForm
              payload={{
                subject: campaign?.subject,
                body: campaign?.bodyTemplate || campaign?.body || "",
                bodyTemplate: campaign?.bodyTemplate || campaign?.body || "",
                campaignId: campaign?.id,
              }}
              recipients={campaign?.recipients?.map((r) => ({
                name: r.name,
                email: r.email,
              }))}
            >
              <button
                type="button"
                className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-rounded-xl tw-bg-[#5D44FF] hover:tw-bg-[#4e36f0] active:tw-bg-[#4e36f0] tw-text-white tw-font-semibold tw-text-xs tw-shadow-sm tw-transition-all tw-border-0 tw-cursor-pointer tw-shrink-0 active:tw-scale-95 tw-font-inter"
              >
                <RotateCw size={13} className="tw-stroke-[2.5]" />
                <span>Reuse This Email</span>
              </button>
            </EmailForm>
          </div>
        </div>

        {/* Global Job Failure Banner */}
        {campaign?.status?.toUpperCase() === "FAILED" && campaign?.failureReason && (
          <div className="tw-mb-6 tw-rounded-xl tw-bg-red-50 tw-p-4 tw-border tw-border-red-200 tw-flex tw-items-start tw-gap-3">
            <Info className="tw-text-red-500 tw-mt-0.5" size={20} />
            <div>
              <h3 className="tw-text-sm tw-font-semibold tw-text-red-800">Mail Merge Job Failed</h3>
              <p className="tw-text-sm tw-text-red-700 tw-mt-1">{campaign.failureReason}</p>
            </div>
          </div>
        )}

        {/* Top Metric Cards (Recipients & Delivered) */}
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-6">
          {/* Recipients Card */}
          <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-p-5 tw-flex tw-items-center tw-gap-4 tw-shadow-sm">
            <div className="tw-size-11 tw-rounded-xl tw-bg-[#EEF0FF] tw-flex tw-items-center tw-justify-center tw-text-[#5D44FF] tw-shrink-0">
              <Users size={20} className="tw-stroke-[2]" />
            </div>
            <div>
              <div className="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-tracking-wider tw-uppercase tw-font-inter">
                RECIPIENTS
              </div>
              <div className="tw-text-2xl tw-font-bold tw-text-slate-800 tw-font-inter tw-leading-tight tw-mt-0.5">
                {campaign?.recipientsCount ?? campaign?.recipients?.length ?? 0}
              </div>
            </div>
          </div>

          {/* Delivered Card */}
          <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-p-5 tw-flex tw-items-center tw-gap-4 tw-shadow-sm">
            <div className="tw-size-11 tw-rounded-xl tw-bg-[#EEF0FF] tw-flex tw-items-center tw-justify-center tw-text-[#5D44FF] tw-shrink-0">
              <Send size={18} className="tw-stroke-[2]" />
            </div>
            <div>
              <div className="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-tracking-wider tw-uppercase tw-font-inter">
                DELIVERED
              </div>
              <div className="tw-text-2xl tw-font-bold tw-text-slate-800 tw-font-inter tw-leading-tight tw-mt-0.5">
                {campaign?.sentCount || 0}
              </div>
            </div>
          </div>
        </div>

        {/* Middle Section: Email Content & Delivery Response */}
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-5 tw-mb-6">
          {/* Left Column: Email Content */}
          <div className="lg:tw-col-span-8 tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-p-6 tw-shadow-sm tw-flex tw-flex-col">
            <h2 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-font-inter tw-mb-3">
              Email Content
            </h2>

            <div className="tw-space-y-1.5 tw-text-xs tw-font-inter tw-mb-3">
              <div className="tw-flex tw-items-baseline tw-gap-2">
                <span className="tw-text-slate-400 tw-w-14 tw-shrink-0">From:</span>
                <span className="tw-text-slate-800 tw-font-medium">
                  {campaign?.from || "-"}
                </span>
              </div>
              <div className="tw-flex tw-items-baseline tw-gap-2">
                <span className="tw-text-slate-400 tw-w-14 tw-shrink-0">Subject:</span>
                <span className="tw-text-slate-900 tw-font-bold">
                  {campaign?.subject || "-"}
                </span>
              </div>
            </div>

            <div className="tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100 tw-my-2" />

            {/* Email Body Content */}
            <div className="tw-flex-1 tw-py-2">
              {renderFormattedBody(campaign?.bodyTemplate || campaign?.body)}
            </div>
          </div>

          {/* Right Column: Delivery Performance */}
          <div className="lg:tw-col-span-4 tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-p-6 tw-shadow-sm tw-flex tw-flex-col">
            <h2 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-font-inter tw-mb-4">
              Delivery Performance
            </h2>

            {/* Delivery Rate Header */}
            <div className="tw-flex tw-items-center tw-gap-4 tw-mb-5">
              <div className="tw-w-16 tw-h-13 tw-rounded-xl tw-border-2 tw-border-solid tw-border-[#5D44FF] tw-flex tw-items-center tw-justify-center tw-text-base tw-font-bold tw-text-slate-800 tw-bg-white tw-shadow-xs tw-font-inter">
                {deliveryPercentage}%
              </div>
              <div>
                <div className="tw-text-xs tw-font-bold tw-text-slate-800 tw-font-inter tw-mb-1">
                  Delivery Rate
                </div>
                <div className="tw-text-[11px] tw-text-slate-500 tw-font-inter">
                  {campaign?.sentCount || 0} of {campaign?.recipientsCount ?? campaign?.recipients?.length ?? 0} emails sent
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="tw-w-full tw-h-2 tw-bg-slate-100 tw-rounded-full tw-overflow-hidden tw-mb-6">
              <div
                style={{ width: `${deliveryPercentage}%` }}
                className="tw-h-full tw-bg-[#5D44FF] tw-rounded-full tw-transition-all"
              />
            </div>

            {/* Metrics Breakdown */}
            <div className="tw-mt-auto tw-space-y-3 tw-pt-2 tw-border-t tw-border-b-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100">
              <div className="tw-flex tw-items-center tw-justify-between tw-text-xs tw-font-inter">
                <span className="tw-flex tw-items-center tw-gap-2 tw-text-slate-600">
                  <span className="tw-size-2 tw-rounded-full tw-bg-[#5D44FF]" />
                  Delivered
                </span>
                <span className="tw-font-bold tw-text-slate-800">
                  {campaign?.sentCount || 0}
                </span>
              </div>

              <div className="tw-flex tw-items-center tw-justify-between tw-text-xs tw-font-inter">
                <span className="tw-flex tw-items-center tw-gap-2 tw-text-slate-600">
                  <span className="tw-size-2 tw-rounded-full tw-bg-red-400" />
                  Limit Exceeded / Failed
                  {campaign?.failedCount > 0 && (
                    <Tooltip
                      title={
                        campaign?.errorMessage ||
                        campaign?.failureReason ||
                        "Daily email sending limit exceeded. Please upgrade your plan or try again tomorrow."
                      }
                      placement="top"
                    >
                      <span className="tw-inline-flex tw-items-center tw-cursor-pointer tw-text-red-500 hover:tw-text-red-700">
                        <Info size={12} className="tw-stroke-[2.5]" />
                      </span>
                    </Tooltip>
                  )}
                </span>
                <span className="tw-font-bold tw-text-slate-800">
                  {campaign?.failedCount || 0}
                </span>
              </div>

              <div className="tw-flex tw-items-center tw-justify-between tw-text-xs tw-font-inter">
                <span className="tw-flex tw-items-center tw-gap-2 tw-text-slate-600">
                  <span className="tw-size-2 tw-rounded-full tw-bg-slate-300" />
                  Total Recipients
                </span>
                <span className="tw-font-bold tw-text-slate-800">
                  {campaign?.recipientsCount ?? campaign?.recipients?.length ?? 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section: Recipients Table */}
        <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-overflow-hidden tw-shadow-sm">
          {/* Table Header Controls */}
          <div className="tw-px-6 tw-py-4 tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-3 tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100">
            <div className="tw-flex tw-items-center tw-gap-2">
              <h3 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-font-inter tw-mb-0">
                Recipients
              </h3>
              <span className="tw-text-xs tw-text-slate-500 tw-font-normal tw-font-inter">
                ({campaign?.recipients?.length || 0})
              </span>
            </div>

            {/* Search Input */}
            <div className="tw-relative tw-w-full sm:tw-w-72">
              <Search
                size={14}
                className="tw-absolute tw-left-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400"
              />
              <input
                type="text"
                placeholder="Search recipients..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="tw-w-full tw-pl-9 tw-pr-3 tw-py-2 tw-text-xs tw-rounded-xl tw-border tw-border-solid tw-border-slate-200/80 focus:tw-border-[#5D44FF] focus:tw-outline-none tw-font-inter placeholder:tw-text-slate-400 tw-transition-colors"
              />
            </div>
          </div>

          {/* Recipients Table */}
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse tw-text-left">
              <thead>
                <tr className="tw-bg-white tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100 tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-font-inter">
                  <th className="tw-py-3 tw-pl-6">RECIPIENT</th>
                  <th className="tw-py-3 tw-px-4">EMAIL</th>
                  <th className="tw-py-3 tw-px-4">STATUS</th>
                  <th className="tw-py-3 tw-pr-6 tw-text-right">SENT</th>
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-slate-50">
                {paginatedRecipients.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="tw-text-center tw-py-12 tw-text-xs tw-text-slate-400 tw-font-inter"
                    >
                      No recipients match your search.
                    </td>
                  </tr>
                ) : (
                  paginatedRecipients.map((recipient) => (
                    <tr
                      key={recipient.id}
                      className="hover:tw-bg-slate-50/60 tw-transition-colors"
                    >
                      <td className="tw-py-3.5 tw-pl-6">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-size-8 tw-rounded-full tw-bg-[#EEF0FF] tw-text-[#5D44FF] tw-font-bold tw-text-xs tw-flex tw-items-center tw-justify-center tw-shrink-0">
                            {getInitials(recipient.name)}
                          </div>
                          <span className="tw-text-xs tw-font-bold tw-text-slate-800 tw-font-inter">
                            {recipient.name}
                          </span>
                        </div>
                      </td>
                      <td className="tw-py-3.5 tw-px-4 tw-text-xs tw-text-slate-500 tw-font-inter">
                        {recipient.email}
                      </td>
                      <td className="tw-py-3.5 tw-px-4">
                        {renderStatusBadge(recipient.status || "Pending", recipient)}
                      </td>
                      <td className="tw-py-3.5 tw-pr-6 tw-text-right tw-text-xs tw-text-slate-500 tw-font-inter">
                        {formatDateTime(recipient.sentAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer / Pagination */}
          <div className="tw-px-6 tw-py-4 tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-3 tw-border-t tw-border-b-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100 tw-text-xs tw-text-slate-500 tw-font-inter">
            <div>
              Showing{" "}
              <span className="tw-font-bold tw-text-slate-700">
                {filteredRecipients.length > 0
                  ? (currentPage - 1) * pageSize + 1
                  : 0}
              </span>{" "}
              to{" "}
              <span className="tw-font-bold tw-text-slate-700">
                {Math.min(currentPage * pageSize, filteredRecipients.length)}
              </span>{" "}
              of{" "}
              <span className="tw-font-bold tw-text-slate-700">
                {filteredRecipients.length}
              </span>{" "}
              recipients
            </div>

            <div className="tw-flex tw-items-center tw-gap-2">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                className="tw-px-3.5 tw-py-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white hover:tw-bg-slate-50 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-text-slate-700 tw-font-semibold tw-text-xs tw-transition-all tw-cursor-pointer tw-font-inter"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                className="tw-px-3.5 tw-py-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white hover:tw-bg-slate-50 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-text-slate-700 tw-font-semibold tw-text-xs tw-transition-all tw-cursor-pointer tw-font-inter"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MailMergeDetails;
